import { facingToward } from '../../rules/movement.js';
import { hashNoise } from '../../world/noise.js';
import { RUNESMITH_BIOMES } from '../affixes.js';
import { BIOMES } from '../biomes.js';
import { footprintOf, PROPS, silhouetteOf } from '../props.js';
import { GRASS, terrainOf } from '../terrains.js';
import { cheapestPath } from './path-cost.js';
import { buildCompanyQuarter } from './world-company.js';
import { clearDisc } from './world-terrain.js';
/** The capital of a 384-tile world (ADR 0028): room for its every building and a dozen homes. */
const CAPITAL_RADIUS = 26;
const HAMLET_RADIUS = 19;
/** Where the road crosses into the village: the gate, a few tiles inside the cleared disc. */
const GATE_INSET = 3;
/** Where each outer biome's hamlet lies, as an angle from the capital. */
const HAMLET_BEARING = {
    snow: -Math.PI / 2,
    swamp: 0,
    desert: Math.PI / 2,
    forest: Math.PI,
};
/** Crop plots belong to the meadow and forest; the marsh, desert and snow
 * villages get their character from their own ground and vegetation. */
const NO_FARMS = new Set(['swamp', 'desert', 'snow']);
const FIELDS = ['field-wheat', 'field-garden'];
/** Choose where the villages stand and clear their ground. */
export function siteVillages(fields) {
    const villages = [
        {
            name: BIOMES.meadow.village,
            biome: 'meadow',
            centre: { ...fields.centre },
            radius: CAPITAL_RADIUS,
            capital: true,
        },
    ];
    for (const [biome, bearing] of Object.entries(HAMLET_BEARING)) {
        const site = findSite(fields, biome, bearing);
        if (site === null)
            continue;
        villages.push({
            name: BIOMES[biome].village,
            biome,
            centre: site,
            radius: HAMLET_RADIUS,
            capital: false,
        });
    }
    for (const village of villages) {
        clearDisc(fields, village.centre, village.radius);
        // The square of trodden earth.
        const plaza = village.capital ? 4 : 2;
        clearDisc(fields, village.centre, plaza, 'd');
    }
    return villages;
}
/**
 * The hamlet sits on its biome's bearing, about two thirds of the way to
 * the rim; the nearest tile truly inside the biome and clear of the rim
 * wins, searched outward in rings.
 */
function findSite(fields, biome, bearing) {
    const half = fields.size / 2;
    const ideal = {
        x: Math.round(fields.centre.x + Math.cos(bearing) * half * 0.62),
        y: Math.round(fields.centre.y + Math.sin(bearing) * half * 0.62),
    };
    for (let ring = 0; ring < 40; ring += 1) {
        for (let dy = -ring; dy <= ring; dy += 1) {
            for (let dx = -ring; dx <= ring; dx += 1) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring)
                    continue;
                const x = ideal.x + dx;
                const y = ideal.y + dy;
                const edge = Math.min(x, y, fields.size - 1 - x, fields.size - 1 - y);
                if (edge < HAMLET_RADIUS + 12)
                    continue;
                if (!allBiome(fields, x, y, HAMLET_RADIUS + 2, biome))
                    continue;
                return { x, y };
            }
        }
    }
    return null;
}
function allBiome(fields, cx, cy, radius, biome) {
    for (let y = cy - radius; y <= cy + radius; y += 2) {
        for (let x = cx - radius; x <= cx + radius; x += 2) {
            if (fields.biomeAt(x, y) !== biome)
                return false;
        }
    }
    return true;
}
/** Building sites are relative to the portal. The first sites hold public
 * buildings and keepers; the rest hold homes. The search below may move a
 * site a few tiles to clear an existing road, cliff or roof. */
const VILLAGE_PLANS = {
    // North-west of the capital's square stands the Company's bastion (ADR
    // 0067, world-company.ts): the foundry and a home keep out of its way.
    meadow: [
        [-13, -10],
        [13, -10],
        [-13, 2],
        [13, 2],
        [-5, 10],
        [4, 12],
        [-17, 13],
        [17, 13],
        [-17, -1],
        [17, -1],
        [-7, 16],
        [10, 17],
        [-17, -17],
        [9, -19],
        [0, 22],
        [-21, 6],
        [21, 6],
    ],
    forest: [
        [-11, -9],
        [11, -9],
        [-12, 5],
        [11, 5],
        [-1, 11],
        [-12, 14],
        [11, 14],
        [-2, -16],
        [2, 17],
    ],
    swamp: [
        [-12, -9],
        [9, -9],
        [-12, 3],
        [10, 3],
        [-2, 11],
        [-12, 13],
        [11, 13],
        [-2, -16],
        [1, 17],
    ],
    desert: [
        [-11, -8],
        [11, -8],
        [-11, 4],
        [11, 4],
        [-2, 12],
        [-12, 13],
        [11, 13],
        [-1, -16],
        [1, 17],
    ],
    snow: [
        [-9, -9],
        [9, -9],
        [-12, 3],
        [11, 3],
        [-1, 11],
        [-12, 13],
        [11, 13],
        [0, -16],
        [1, 17],
    ],
};
export function buildVillage(fields, village, occupancy) {
    const props = [];
    const folk = [];
    const { centre } = village;
    const onRoad = (box, margin) => {
        for (let y = box.y0 - margin; y <= box.y1 + margin; y += 1) {
            for (let x = box.x0 - margin; x <= box.x1 + margin; x += 1) {
                if (!fields.inside(x, y))
                    continue;
                // The square is trodden earth too, but it is the square, not a road.
                if (Math.hypot(x - centre.x, y - centre.y) <= (village.capital ? 5 : 3))
                    continue;
                if (fields.terrain[fields.index(x, y)] === 'd')
                    return true;
            }
        }
        return false;
    };
    // What each piece covers on screen, roof included: a roof may not hide
    // the front of the building behind it, nor a field.
    const silhouettes = [];
    const doored = dooredOf(BIOMES[village.biome]);
    // Where the lanes must reach (`laneToDoors`): the tile before every door.
    const doorsteps = new Set();
    /** Whether the piece may stand here, without putting it. */
    const canPut = (placed) => {
        const { prop, x, y } = placed;
        const shape = silhouetteOf(placed);
        if (silhouettes.some((other) => overlaps(shape, other)))
            return false;
        const box = footprintOf(placed);
        if (!occupancy.fits(placed) || onRoad(box, 1))
            return false;
        // A door opens onto a lane: never onto the water, where none can be dug,
        // and no field may come to lie against a doorstep, since a lane skirts
        // a field by a tile.
        if (doored.has(prop) && !doorstepOpen(fields, occupancy, x, y + 1))
            return false;
        if (PROPS[prop]?.blocks !== true) {
            for (let sy = box.y0 - 1; sy <= box.y1 + 1; sy += 1) {
                for (let sx = box.x0 - 1; sx <= box.x1 + 1; sx += 1) {
                    if (fields.inside(sx, sy) && doorsteps.has(fields.index(sx, sy)))
                        return false;
                }
            }
        }
        return true;
    };
    const put = (prop, x, y, flip) => {
        const placed = { prop, x, y, flip: flip ?? hashNoise(x, y, fields.seed + 77) < 0.5 };
        if (!canPut(placed))
            return false;
        const shape = silhouetteOf(placed);
        if (doored.has(prop))
            doorsteps.add(fields.index(x, y + 1));
        occupancy.take(placed);
        props.push(placed);
        silhouettes.push(shape);
        return true;
    };
    // The portal is the heart of the square (ADR 0017): on the tile the roads
    // lead to, so it is not held to the rule that keeps buildings off them.
    const arch = { prop: 'portal', x: centre.x, y: centre.y, flip: false };
    let portal;
    if (occupancy.fits(arch)) {
        occupancy.take(arch);
        props.push(arch);
        silhouettes.push(silhouetteOf(arch));
        portal = { x: centre.x, y: centre.y, arrival: { x: centre.x, y: centre.y + 1 } };
    }
    // The Givrecoeur Company's bastion (ADR 0067), before anything else is
    // built: the heart of the story takes the best ground by the square, and
    // the houses, the well and the stalls make room for it.
    let quarter;
    if (village.capital) {
        const built = buildCompanyQuarter(fields, village, occupancy, {
            claim: (placed) => {
                props.push(placed);
                silhouettes.push(silhouetteOf(placed));
            },
            clear: (box) => !silhouettes.some((other) => overlaps(box, other)),
        });
        if (built !== undefined) {
            folk.push(...built.folk);
            quarter = built.centre;
        }
    }
    // The well at a corner of the square, before the stalls, which make way.
    for (const [dx, dy] of WELL_SITES) {
        if (put('well', centre.x + dx, centre.y + dy, false))
            break;
    }
    const biome = BIOMES[village.biome];
    const houses = biome.houses;
    const vary = (salt) => hashNoise(centre.x, centre.y, fields.seed + salt);
    const buildings = village.capital ? biome.buildings : biome.buildings.slice(0, 2);
    const homes = village.capital ? 6 + Math.floor(vary(81) * 3) : 3 + Math.floor(vary(82) * 2);
    const needed = [
        ...buildings,
        biome.services.shop,
        biome.services.foundry,
        biome.services.alchemy,
        biome.services.auction,
        ...Array.from({ length: homes }, (_, i) => houses[Math.floor(vary(80 + i) * houses.length)] ?? 'house-red'),
    ];
    let farthest = 0;
    const inside = (box) => [box.x0, box.x1].every((x) => [box.y0, box.y1].every((y) => Math.hypot(x - centre.x, y - centre.y) <= village.radius - 2));
    const plan = VILLAGE_PLANS[village.biome];
    for (const [index, house] of needed.entries()) {
        const [dx, dy] = plan[index] ?? [0, village.radius - 4];
        const wantedX = centre.x + dx;
        const wantedY = centre.y + dy;
        const candidates = [];
        for (let y = centre.y - village.radius; y <= centre.y + village.radius; y += 1) {
            for (let x = centre.x - village.radius; x <= centre.x + village.radius; x += 1) {
                if (inside(footprintOf({ prop: house, x, y })))
                    candidates.push({ x, y });
            }
        }
        candidates.sort((a, b) => {
            const da = (a.x - wantedX) ** 2 + (a.y - wantedY) ** 2;
            const db = (b.x - wantedX) ** 2 + (b.y - wantedY) ** 2;
            return da - db || a.y - b.y || a.x - b.x;
        });
        for (const { x, y } of candidates) {
            if (!put(house, x, y))
                continue;
            farthest = Math.max(farthest, Math.hypot(x - centre.x, y - centre.y));
            break;
        }
    }
    // The market: stalls on the square, and the villagers who keep them.
    // The capital's stay north of the arrival row: a newcomer walks off it freely.
    const stalls = village.capital
        ? [
            [-4, -1],
            [4, -1],
            [0, -4],
        ]
        : village.biome === 'desert'
            ? [
                [3, 1],
                [-3, 1],
            ]
            : village.biome === 'forest'
                ? [[-3, 1]]
                : [];
    for (const [dx, dy] of stalls) {
        put('stall', centre.x + (dx ?? 0), centre.y + (dy ?? 0));
    }
    // The new public buildings can move close to the market. Pick an actual
    // open tile rather than assuming the old north-west spot stays free.
    for (const [dx, dy] of [
        [-2, -3],
        [-3, -3],
        [-2, -4],
        [-3, -4],
        [-4, -2],
        [2, -3],
        [3, -3],
    ]) {
        const x = centre.x + dx;
        const y = centre.y + dy;
        if (!fields.inside(x, y) || occupancy.blocked[fields.index(x, y)] === 1)
            continue;
        const ground = fields.terrain[fields.index(x, y)] ?? GRASS;
        if (!terrainOf(ground).walkable || ground === 'b')
            continue;
        folk.push({ mob: 'villager', x, y, count: village.capital ? 2 : 1, radius: 2 });
        break;
    }
    // The region's quest giver (ADR 0047) on the square, a step from where
    // travellers arrive, so the first face a newcomer meets hands out work.
    if (biome.questGiver !== undefined) {
        for (const [dx, dy] of QUEST_GIVER_SITES) {
            const x = centre.x + dx;
            const y = centre.y + dy;
            if (!fields.inside(x, y) || occupancy.blocked[fields.index(x, y)] === 1)
                continue;
            const ground = fields.terrain[fields.index(x, y)] ?? GRASS;
            if (!terrainOf(ground).walkable || ground === 'b')
                continue;
            folk.push({ mob: biome.questGiver, x, y, count: 1, radius: 0, facing: 'south' });
            break;
        }
    }
    if (!NO_FARMS.has(village.biome)) {
        folk.push(...buildFarms(fields, village, farthest, village.capital ? 2 : 1, put));
    }
    laneToDoors(fields, village, occupancy, props);
    dressVillage(fields, village, put);
    const smith = keeperAt(fields, occupancy, props, biome.services.forge, 'smith');
    if (smith !== undefined)
        folk.push(smith);
    // The runesmith (ADR 0057) works beside the smith, on the other side of his door.
    if (RUNESMITH_BIOMES.includes(village.biome)) {
        const taken = smith === undefined ? [] : [smith];
        const runesmith = keeperAt(fields, occupancy, props, biome.services.forge, 'runesmith', taken);
        if (runesmith !== undefined)
            folk.push(runesmith);
    }
    const merchant = keeperAt(fields, occupancy, props, biome.services.shop, 'merchant');
    if (merchant !== undefined)
        folk.push(merchant);
    const smelter = keeperAt(fields, occupancy, props, biome.services.foundry, 'smelter');
    if (smelter !== undefined)
        folk.push(smelter);
    const alchemist = keeperAt(fields, occupancy, props, biome.services.alchemy, 'alchemist');
    if (alchemist !== undefined)
        folk.push(alchemist);
    const auctionSign = keeperAt(fields, occupancy, props, biome.services.auction, 'auction-sign');
    if (auctionSign !== undefined)
        folk.push(auctionSign);
    // The account's chest (ADR 0079) on the other side of the hall's door: what
    // one keeps lies by where one sells. A townsperson, so it blocks no lane.
    const chest = keeperAt(fields, occupancy, props, biome.services.auction, 'stash-chest', auctionSign === undefined ? [] : [auctionSign]);
    if (chest !== undefined)
        folk.push(chest);
    return {
        props,
        folk,
        ...(portal !== undefined ? { portal } : {}),
        ...(quarter !== undefined ? { quarter } : {}),
    };
}
/** Small same-species groves soften the cleared edge. They follow the world's
 * grouping rule instead of scattering unrelated trees around the village. */
function dressVillage(fields, village, put) {
    const accent = {
        forest: 'oak',
        swamp: 'reeds',
        desert: 'palm',
        snow: 'campfire',
    };
    const signature = accent[village.biome];
    if (signature !== undefined) {
        for (const [dx, dy] of [
            [-5, -3],
            [5, -3],
            [-5, 3],
            [5, 3],
        ]) {
            if (put(signature, village.centre.x + dx, village.centre.y + dy))
                break;
        }
    }
    const groves = {
        meadow: ['oak', 'bush', 'oak', 'bush'],
        forest: ['oak', 'pine', 'oak', 'pine'],
        swamp: ['dead-tree', 'dead-tree', 'dead-tree'],
        desert: ['palm', 'cactus', 'palm'],
        snow: ['snowy-pine', 'snowy-pine', 'snowy-pine'],
    };
    const { centre, radius } = village;
    for (const [i, prop] of groves[village.biome].entries()) {
        const count = groves[village.biome].length;
        const angle = ((i + 0.3) * Math.PI * 2) / count;
        for (const side of [-3, 0, 3]) {
            const reach = radius - 4 + hashNoise(i, side, fields.seed + 302) * 2;
            const x = Math.round(centre.x + Math.cos(angle) * reach - Math.sin(angle) * side);
            const y = Math.round(centre.y + Math.sin(angle) * reach + Math.cos(angle) * side);
            put(prop, x, y);
        }
    }
}
/**
 * Sites for the well, at the corners of the square around the portal: off
 * the row where newcomers arrive and walk away from the shrine first.
 */
const WELL_SITES = [
    [3, -3],
    [-4, -3],
    // The capital's north-west corner is the Company's way (ADR 0067).
    [3, -4],
    [3, 2],
    [-4, 2],
    [2, 3],
    [-3, 3],
];
/** Where the quest giver stands, beside the arch and off the arrival tile. */
const QUEST_GIVER_SITES = [
    [2, 1],
    [-2, 1],
    [2, 2],
    [-2, 2],
    [3, 1],
    [-3, 1],
];
const BESIDE_THE_DOOR = [
    [2, 1],
    [-2, 1],
    [3, 1],
    [-3, 1],
    [2, 2],
    [-2, 2],
];
const AT_THE_DOOR = [[0, 1], [0, 2], ...BESIDE_THE_DOOR];
/**
 * A keeper at his building (the smith at the forge, ADR 0013; the merchant at
 * his shop, ADR 0016; the smelter at the foundry, ADR 0018; the alchemist at
 * his house, ADR 0026): in front of its south wall, on the first of a few
 * tiles one can stand on. The smith and the merchant stand beside the door,
 * where their forge and stall are drawn; the smelter and the alchemist stand
 * on the doorstep, their door being the middle of the facade. The auction
 * house's signpost stands beside its door, which stays clear to walk in, and
 * the account's chest (ADR 0079) on the door's other side.
 * Players walk through townsfolk, so no lane is cut.
 */
function keeperAt(fields, occupancy, props, building, mob, taken = []) {
    const home = props.find((placed) => placed.prop === building);
    if (home === undefined)
        return undefined;
    for (const [dx, dy] of mob === 'smelter' || mob === 'alchemist' ? AT_THE_DOOR : BESIDE_THE_DOOR) {
        const x = home.x + dx;
        const y = home.y + dy;
        if (!fields.inside(x, y))
            continue;
        if (taken.some((tile) => tile.x === x && tile.y === y))
            continue;
        const i = fields.index(x, y);
        if (occupancy.blocked[i] === 1)
            continue;
        const char = fields.terrain[i] ?? GRASS;
        if (!terrainOf(char).walkable || char === 'b')
            continue;
        return { mob, x, y, count: 1, radius: 0, facing: 'south' };
    }
    return undefined;
}
/**
 * Fields on the outskirts, past the last house and inside the cleared disc:
 * a crop plot, a haystack at its corner, and its farmer, who works that plot
 * and nowhere else. Tried at hashed angles until enough fit.
 */
function buildFarms(fields, village, farthest, wanted, put) {
    const { centre } = village;
    const farmers = [];
    const start = hashNoise(centre.x, centre.y, fields.seed + 91) * Math.PI * 2;
    const inner = Math.min(farthest + 4, village.radius - 6);
    for (let k = 0; k < 24 && farmers.length < wanted; k += 1) {
        const angle = start + (k * Math.PI * 2) / 12 + (k >= 12 ? Math.PI / 12 : 0);
        const reach = inner + (k >= 12 ? 2 : 0);
        const x = Math.round(centre.x + Math.cos(angle) * reach);
        const y = Math.round(centre.y + Math.sin(angle) * reach);
        if (Math.hypot(x - centre.x, y - centre.y) > village.radius - 3)
            continue;
        const crop = FIELDS[Math.floor(hashNoise(x, y, fields.seed + 92) * FIELDS.length)] ?? 'field-wheat';
        if (!put(crop, x, y, false))
            continue;
        // A haystack at one end of the field, when there is room, and the
        // village's barn beside its first field.
        const side = x < centre.x ? -1 : 1;
        put('haystack', x + side * 4, y);
        const barn = BIOMES[village.biome].barn;
        if (barn !== undefined && farmers.length === 0) {
            const spots = [
                [-side * 7, 0],
                [0, -5],
                [0, 6],
                [side * 9, 0],
            ];
            for (const [dx, dy] of spots)
                if (put(barn, x + (dx ?? 0), y + (dy ?? 0)))
                    break;
        }
        // The field's middle row: the farmer walks up and down it.
        farmers.push({ mob: 'farmer', x, y: y - 1, count: 1, radius: 1 });
    }
    return farmers;
}
function overlaps(a, b) {
    return a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1;
}
/**
 * A lane of trodden earth from every door to the village's roads: the tile
 * below the middle of a building's front is its doorstep, and the cheapest
 * way from there to the square or a road is dug, around the buildings and
 * the fields, never beside water. Door by door, each lane joins the ones
 * before, so they branch like paths people wear.
 */
function laneToDoors(fields, village, occupancy, props) {
    const { size } = fields;
    const { centre } = village;
    const doored = dooredOf(BIOMES[village.biome]);
    // Far enough for every door: a barn beside the outermost field can stand
    // past the village's own radius.
    const reach = props.reduce((far, placed) => doored.has(placed.prop)
        ? Math.max(far, Math.ceil(Math.hypot(placed.x - centre.x, placed.y + 1 - centre.y)) + 2)
        : far, village.radius + 2);
    const cost = new Float32Array(size * size).fill(Infinity);
    const network = [];
    for (let y = centre.y - reach; y <= centre.y + reach; y += 1) {
        for (let x = centre.x - reach; x <= centre.x + reach; x += 1) {
            if (!fields.inside(x, y) || Math.hypot(x - centre.x, y - centre.y) > reach)
                continue;
            const i = fields.index(x, y);
            const char = fields.terrain[i] ?? GRASS;
            if (occupancy.blocked[i] === 1 || fields.locked[i] === 1)
                continue;
            // A lane skirts a field by a tile: the ground spreads half a tile, and
            // earth dug against the crops would swallow the plot's edge.
            if (touchesCover(occupancy, fields, x, y))
                continue;
            if (!terrainOf(char).walkable || char === 'b' || nearWater(fields, x, y))
                continue;
            if (char === 'd') {
                cost[i] = 0.2;
                network.push(i);
            }
            else {
                // Off the walls: a lane hugging a house reads as a moat of mud.
                cost[i] = besideWall(occupancy, fields, x, y) ? 4 : 1;
            }
        }
    }
    // Near a lane but not on it costs dear: a tile of earth spreads to about
    // two on screen, so a lane two tiles from another runs into it as a slab
    // of mud. A new lane joins the old one instead of running alongside.
    const penalised = new Uint8Array(size * size);
    const shun = (i) => {
        const x = i % size;
        const y = (i - x) / size;
        for (let dy = -2; dy <= 2; dy += 1) {
            for (let dx = -2; dx <= 2; dx += 1) {
                if (!fields.inside(x + dx, y + dy))
                    continue;
                const j = fields.index(x + dx, y + dy);
                const here = cost[j] ?? Infinity;
                if (penalised[j] === 1 || !Number.isFinite(here) || here < 1)
                    continue;
                cost[j] = here + 3;
                penalised[j] = 1;
            }
        }
    };
    for (const i of network)
        shun(i);
    if (network.length === 0)
        return;
    for (const placed of props) {
        if (!doored.has(placed.prop))
            continue;
        const door = fields.index(placed.x, placed.y + 1);
        if (!Number.isFinite(cost[door] ?? Infinity))
            continue;
        const lane = cheapestPath({ width: size, height: size, cost }, network, door);
        if (lane === null)
            continue;
        for (const i of lane) {
            if (fields.locked[i] !== 1)
                fields.terrain[i] = 'd';
            cost[i] = 0.2;
            network.push(i);
        }
        for (const i of lane)
            shun(i);
    }
}
/** Every prop of the biome with a door on its south wall. */
function dooredOf(biome) {
    return new Set([
        ...biome.houses,
        ...biome.buildings,
        biome.services.shop,
        biome.services.foundry,
        biome.services.alchemy,
        biome.services.auction,
        ...(biome.barn ? [biome.barn] : []),
        // The Company's bastion (ADR 0067): a lane is dug to the foot of its way.
        'company-avenue',
    ]);
}
/** Whether a lane could be dug on this tile: open ground, dry, nothing built on it. */
function doorstepOpen(fields, occupancy, x, y) {
    if (!fields.inside(x, y))
        return false;
    const i = fields.index(x, y);
    const char = fields.terrain[i] ?? GRASS;
    return (terrainOf(char).walkable &&
        char !== 'b' &&
        occupancy.blocked[i] !== 1 &&
        !nearWater(fields, x, y) &&
        !touchesCover(occupancy, fields, x, y));
}
function besideWall(occupancy, fields, cx, cy) {
    for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, -1],
    ]) {
        if (fields.inside(cx + dx, cy + dy) &&
            occupancy.blocked[fields.index(cx + dx, cy + dy)] === 1) {
            return true;
        }
    }
    return false;
}
function touchesCover(occupancy, fields, cx, cy) {
    for (let y = cy - 1; y <= cy + 1; y += 1) {
        for (let x = cx - 1; x <= cx + 1; x += 1) {
            if (fields.inside(x, y) && occupancy.cover[fields.index(x, y)] === 1)
                return true;
        }
    }
    return false;
}
function nearWater(fields, cx, cy) {
    for (let y = cy - 1; y <= cy + 1; y += 1) {
        for (let x = cx - 1; x <= cx + 1; x += 1) {
            if (!fields.inside(x, y))
                continue;
            const char = fields.terrain[fields.index(x, y)];
            if (char === 'w' || char === 'i' || char === 'm' || char === 'b')
                return true;
        }
    }
    return false;
}
/**
 * Guards at every way in, once every road and footpath is dug (a place of
 * interest's path may come in where no road does). A post is free ground
 * beside the way; it is kept clear so nothing grows on it.
 */
export function guardGates(fields, village, occupancy) {
    const guards = [];
    for (const gate of findGates(fields, village)) {
        for (const post of gate.posts) {
            const i = fields.index(post.x, post.y);
            if (occupancy.blocked[i] === 1 || occupancy.cover[i] === 1)
                continue;
            occupancy.reserveDisc(post.x, post.y, 1);
            guards.push({ mob: 'guard', x: post.x, y: post.y, count: 1, radius: 0, facing: gate.facing });
        }
    }
    return guards;
}
/**
 * Where the roads come in: road tiles on a ring just inside the village's
 * edge, grouped into runs, one gate per run. The capital posts a guard on
 * each side of the road, a hamlet one. A post is the first open tile off
 * the road, perpendicular to it, so the guards never stand in the way.
 */
export function findGates(fields, village) {
    const { centre } = village;
    const ring = village.radius - GATE_INSET;
    const crossings = [];
    for (let y = centre.y - ring - 1; y <= centre.y + ring + 1; y += 1) {
        for (let x = centre.x - ring - 1; x <= centre.x + ring + 1; x += 1) {
            if (!fields.inside(x, y))
                continue;
            if (Math.abs(Math.hypot(x - centre.x, y - centre.y) - ring) > 0.5)
                continue;
            if (fields.terrain[fields.index(x, y)] !== 'd')
                continue;
            crossings.push({ tile: { x, y }, angle: Math.atan2(y - centre.y, x - centre.x) });
        }
    }
    crossings.sort((a, b) => a.angle - b.angle);
    // Runs of neighbouring crossings: one road may be two or three tiles wide.
    const runs = [];
    for (const crossing of crossings) {
        const last = runs.at(-1);
        const previous = last?.at(-1);
        if (last !== undefined &&
            previous !== undefined &&
            Math.max(Math.abs(previous.x - crossing.tile.x), Math.abs(previous.y - crossing.tile.y)) <= 1) {
            last.push(crossing.tile);
        }
        else {
            runs.push([crossing.tile]);
        }
    }
    // The circle closes: a run cut by the -π/π seam is one road.
    const first = runs[0];
    const final = runs.at(-1);
    if (runs.length > 1 && first !== undefined && final !== undefined) {
        const a = first[0];
        const b = final.at(-1);
        if (a !== undefined &&
            b !== undefined &&
            Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) <= 1) {
            runs[0] = [...final, ...first];
            runs.pop();
        }
    }
    const gates = [];
    for (const run of runs) {
        const middle = run[Math.floor(run.length / 2)];
        if (middle === undefined || !leavesVillage(fields, village, middle))
            continue;
        const out = { x: middle.x - centre.x, y: middle.y - centre.y };
        const length = Math.hypot(out.x, out.y) || 1;
        const across = { x: -out.y / length, y: out.x / length };
        const posts = [];
        for (const side of village.capital ? [1, -1] : [1]) {
            const post = firstOffRoad(fields, middle, across, side);
            if (post !== null)
                posts.push(post);
        }
        if (posts.length === 0)
            continue;
        const outward = { x: middle.x + out.x, y: middle.y + out.y };
        gates.push({ posts, facing: facingToward(middle, outward) ?? 'south' });
    }
    return gates;
}
/**
 * Whether the earth at `from` leads out of the village, as a road or a
 * footpath does, rather than being a lane between two houses that happens
 * to cross the gate ring: follow the earth outward only, and see if it gets
 * past the village's edge.
 */
function leavesVillage(fields, village, from) {
    const { centre } = village;
    const ring = Math.hypot(from.x - centre.x, from.y - centre.y);
    const seen = new Set([fields.index(from.x, from.y)]);
    const stack = [from];
    while (stack.length > 0) {
        const tile = stack.pop();
        if (tile === undefined)
            break;
        if (Math.hypot(tile.x - centre.x, tile.y - centre.y) > village.radius + 2)
            return true;
        for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]) {
            const next = { x: tile.x + dx, y: tile.y + dy };
            if (!fields.inside(next.x, next.y))
                continue;
            const i = fields.index(next.x, next.y);
            if (seen.has(i) || fields.terrain[i] !== 'd')
                continue;
            if (Math.hypot(next.x - centre.x, next.y - centre.y) < ring - 0.5)
                continue;
            seen.add(i);
            stack.push(next);
        }
    }
    return false;
}
function firstOffRoad(fields, from, across, side) {
    for (let step = 1; step <= 4; step += 1) {
        const x = Math.round(from.x + across.x * side * step);
        const y = Math.round(from.y + across.y * side * step);
        if (!fields.inside(x, y))
            return null;
        const char = fields.terrain[fields.index(x, y)] ?? GRASS;
        if (char === 'd')
            continue;
        // The ground was cleared with the village: open, unless the road's edge met water.
        return terrainOf(char).walkable && char !== 'b' ? { x, y } : null;
    }
    return null;
}
/** Where a newcomer lands: just south of the capital's portal, one row below travellers. */
export function arrivalOf(village) {
    return {
        spawn: { x: village.centre.x, y: village.centre.y + 2 },
        respawn: { x: village.centre.x - 2, y: village.centre.y + 2 },
    };
}
//# sourceMappingURL=world-villages.js.map